<script lang="ts" setup>
import type { Group, GroupMember } from '~/models/Group'
import { useI18n } from 'vue-i18n'
import { computed, onMounted, ref, watch } from 'vue'
import useGroupsStore from '~/stores/groups'
import { UserRole } from '~/models/User/UserRole'
import { GroupRole, GroupSource, GroupType } from '~/models/Group'
import SettingsGroupCreate from '~/components/settings/SettingsGroups/SettingsGroupCreate.vue'
import SettingsGroup from '~/components/settings/SettingsGroups/SettingsGroup.vue'

const props = defineProps<{ group: Group }>()

const emit = defineEmits(['deleteGroup', 'back', 'groupCreated'])

const { t } = useI18n()

const groupMembers = ref<GroupMember[]>([])
const edit = ref(false)
const copy = ref(false)

const groupCopy = computed(() => ({
  ...props.group,
  name: t('groups.copyOf', { groupName: props.group.name }),
  groupId: '',
  groupSource: GroupSource.Local,
  groupType: GroupType.Adhoc,
} as Group))

const groupMembersCopy = computed(() => groupMembers.value.map(member => ({
  ...member,
  groupMemberId: '',
  groupRole: member.role === UserRole.Teacher ? GroupRole.Admin : GroupRole.Member,
})) as GroupMember[])

const groupsStore = useGroupsStore()
const { loadGroupMembers, findGroupMembers } = groupsStore

const setEdit = () => edit.value = !edit.value
const setCopy = () => copy.value = !copy.value

onMounted(() => {
  if (!Object.keys(props.group).length) {
    setEdit()
    return
  }
  loadGroupMembers(props.group)
    .finally(() => groupMembers.value = findGroupMembers(props.group))
})

watch(() => edit.value, () => {
  if (!edit.value) {
    loadGroupMembers(props.group)
      .finally(() => groupMembers.value = findGroupMembers(props.group))
  }
})

const reemitGroupCreated = (group: Group) => {
  emit('groupCreated', group)
}
</script>

<template>
  <SettingsGroup
    v-if="!edit && !copy"
    :group="group"
    :group-members="groupMembers"
    @delete-group="emit('deleteGroup')"
    @change-view="setEdit"
    @copy-group="setCopy"
  />
  <SettingsGroupCreate
    v-else-if="edit"
    :group="group"
    :group-members="groupMembers"
    @back="emit('back')"
    @change-view="setEdit"
    @group-created="reemitGroupCreated"
  />
  <SettingsGroupCreate
    v-else-if="copy"
    :group="groupCopy"
    :group-members="groupMembersCopy"
    @back="emit('back')"
    @change-view="setCopy"
    @group-created="reemitGroupCreated"
  />
</template>
